import { Box, Button, CircularProgress, Collapse, Divider, Grid, Paper, styled, Tab, Tabs } from "@mui/material";
import { get, includes, startCase } from "lodash";
import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import CustomDataGridFilterToolbar from "../../../common/js/components/CustomDataGridFilterToolbar";
import Snackbar from "../../../common/js/components/KMSnackbar";
import ResearchExportModal from "../research/ResearchExportModal";
import ResearchSnapshotModal from "../research/ResearchSnapshotModal";
import NameSearchModal from "../search/NameSearchModal";
import SavedSearchesModal from "../search/SavedSearchesModal";
import { noPhotoUrl } from "../utils/constants";
import "./Properties.css";
import PropertiesFilters from "./PropertiesFilters";
import PropertyRequestModal from "./PropertyRequestModal";
import ReviewPropertyGrid from "./ReviewPropertyGrid";

const StyledPageToolbar = styled(Grid)({
    marginBottom: "20px",
    marginTop: "20px",
});

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledPaperSpacing = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledLoadingPlaceholder = styled("div")({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
});

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledDivider = styled(Divider)({
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    margin: "0 -20px 0 -20px",
});

const StyledTab = styled(Tab)({
    borderBottom: "none",
    marginRight: "20px",
});

const StyledTabs = styled(Tabs)({
    borderBottom: "none",
    marginRight: "20px",
    width: "20em",
    bottom: 0,
});

const ReviewProperties = (props) => {
    const {
        clearFilters,
        elementsSaving,
        errors,
        handleDealTypeRadioClick,
        handleDeleteCrumb,
        handleSearchSubmit,
        handleSelectAllPropertyType,
        handleSelectMarket,
        handleSelectMicromarket,
        handleSelectSubmarket,
        handleSizeRadioClick,
        handleStatusRadioClick,
        handleSubmitPropertyRequest,
        loadedSearchCrumbs,
        loadMoreSearchResults,
        marketFilterExpansionPanels,
        nameSearchModal,
        properties,
        propertyRequestModal,
        propertySearch,
        propertySearchFilters,
        propertyTab,
        propertyTypeExpansionPanels,
        role,
        savedSearchModal,
        searchCriteria,
        switchTab,
        toggleMarketFilterExpansionPanel,
        togglePropertyRequestModal,
        togglePropertyTypeExpansionPanel,
        updateFilter,
        updatePropertyRequest,
        getProperties,
        setNeedsReviewFilter,
        setNeedsReviewPage,
    } = props;

    const [researchSnapshotModalOpen, setResearchSnapshotModalOpen] = useState(false);
    const [researchExportModalOpen, setResearchExportModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const activeTabProperties = properties[propertyTab] ? properties[propertyTab] : [];

    const filterColumns = [
        { field: "propertyName", label: "Property Name", type: "plaintext" },
        { field: "addressLine", label: "Address Line", type: "plaintext" },
        { field: "city", label: "City", type: "plaintext" },
        { field: "state", label: "State", type: "plaintext" },
        { field: "zipCode", label: "ZipCode", type: "plaintext" },
        { field: "requester", label: "Requester", type: "plaintext" },
        { field: "auditLastModifiedTimestamp", label: "Modified Date", type: "date" },
        { field: "propertyType", label: "Property Type", type: "plaintext" },
        { field: "propertyStatus", label: "Status", type: "plaintext" },
    ];

    const handleOpenResearchSnapshotModal = () => setResearchSnapshotModalOpen(true);
    const handleCloseResearchSnapshotModal = () => setResearchSnapshotModalOpen(false);
    const handleOpenResearchExportModal = () => setResearchExportModalOpen(true);
    const handleCloseResearchExportModal = () => setResearchExportModalOpen(false);

    const handleResearchSnapshotResponse = (result) => {
        if (result === true) {
            setSnackbarMessage("Success: Your request will be processed.");
            setSnackbarVariant("success");
            setSnackbarOpen(true);
        } else if (result === false) {
            setSnackbarMessage("Failed: Your request encountered an error.");
            setSnackbarVariant("error");
            setSnackbarOpen(true);
        }
    };

    const handleResearchExportResponse = (result) => {
        if (result === true) {
            setSnackbarMessage("Success: Your research export was generated.");
            setSnackbarVariant("success");
            setSnackbarOpen(true);
        } else if (result === false) {
            setSnackbarMessage("Failed: Your research export encountered an error.");
            setSnackbarVariant("error");
            setSnackbarOpen(true);
        }
    };

    return (
        <React.Fragment>
            <Collapse in={propertyTab === "all_properties"} unmountOnExit style={{ marginLeft: -20, marginRight: -20 }}>
                <PropertiesFilters
                    updateFilter={updateFilter}
                    searchSubmit={handleSearchSubmit}
                    propertySearchFilters={propertySearchFilters}
                    handleSelectAllPropertyType={handleSelectAllPropertyType}
                    clearFilters={clearFilters}
                    searchCriteria={searchCriteria}
                    handleStatusRadioClick={handleStatusRadioClick}
                    handleSizeRadioClick={handleSizeRadioClick}
                    handleDealTypeRadioClick={handleDealTypeRadioClick}
                    errors={errors}
                    togglePropertyTypeExpansionPanel={togglePropertyTypeExpansionPanel}
                    propertyTypeExpansionPanels={propertyTypeExpansionPanels}
                    toggleMarketFilterExpansionPanel={toggleMarketFilterExpansionPanel}
                    marketFilterExpansionPanels={marketFilterExpansionPanels}
                    handleSelectMarket={handleSelectMarket}
                    handleSelectSubmarket={handleSelectSubmarket}
                    handleSelectMicromarket={handleSelectMicromarket}
                    disabled={true}
                    loadedSearchCrumbs={loadedSearchCrumbs}
                    handleDeleteCrumb={handleDeleteCrumb}
                />
            </Collapse>
            <Snackbar
                open={snackbarOpen}
                message={snackbarMessage}
                variant={snackbarVariant}
                vertical="top"
                horizontal="center"
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            />
            <StyledPageToolbar container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid>
                    <h2 className="page-title">Properties</h2>
                    <div className="page-subtitle">
                        {propertyTab === "all_properties" &&
                            propertySearch.searchResponse &&
                            propertySearch.searchResponse.results &&
                            `Showing  ${propertySearch.searchResponse.results.length} ${
                                propertySearch.searchResponse.results.length === 1
                                    ? "property"
                                    : `properties (${propertySearch.searchResponse.totalResultCount} TOTAL)`
                            }`}
                    </div>
                </Grid>
                <Grid>
                    <Grid container spacing={0} sx={{ alignItems: "center" }}>
                        <Grid>
                            {includes(["dataAdmin", "researchAdmin", "research", "dataEntry"], role) ? (
                                <React.Fragment>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleOpenResearchExportModal()}
                                    >
                                        Export Research
                                    </Button>
                                    <ResearchExportModal
                                        modalOpen={researchExportModalOpen}
                                        closeModal={handleCloseResearchExportModal}
                                        snapshotResponse={handleResearchExportResponse}
                                    />
                                </React.Fragment>
                            ) : (
                                ""
                            )}
                            {includes(["dataAdmin", "researchAdmin"], role) ? (
                                <React.Fragment>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleOpenResearchSnapshotModal()}
                                    >
                                        Capture Research
                                    </Button>
                                    <ResearchSnapshotModal
                                        modalOpen={researchSnapshotModalOpen}
                                        closeModal={handleCloseResearchSnapshotModal}
                                        snapshotResponse={handleResearchSnapshotResponse}
                                    />
                                </React.Fragment>
                            ) : (
                                ""
                            )}
                            <Button variant="contained" color="primary" onClick={() => togglePropertyRequestModal()}>
                                Request Property
                            </Button>
                            <PropertyRequestModal
                                errors={errors}
                                propertyRequestModal={propertyRequestModal}
                                togglePropertyRequestModal={togglePropertyRequestModal}
                                updatePropertyRequest={updatePropertyRequest}
                                handleSubmit={handleSubmitPropertyRequest}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </StyledPageToolbar>
            <StyledPaperSpacing>
                <Grid container sx={{ justifyContent: "space-between" }}>
                    <Grid style={{ position: "relative" }}>
                        <StyledTabs value={propertyTab} onChange={switchTab}>
                            <StyledTab value="all_properties" label="All Properties" />
                            {includes(["dataEntry", "dataAdmin"], role) && (
                                <StyledTab value="data_team_needs_review" label={"Needs Review"} />
                            )}
                        </StyledTabs>
                    </Grid>
                    {propertyTab === "data_team_needs_review" && (
                        <Grid>
                            <CustomDataGridFilterToolbar
                                fields={filterColumns}
                                setFilter={setNeedsReviewFilter}
                                setPage={setNeedsReviewPage}
                                onSubmit={getProperties}
                            />
                        </Grid>
                    )}
                </Grid>
                {propertyTab === "data_team_needs_review" && (
                    <React.Fragment>
                        <StyledDivider />
                        <ReviewPropertyGrid properties={activeTabProperties} {...props} />
                    </React.Fragment>
                )}
                {propertyTab === "all_properties" && (
                    <React.Fragment>
                        <StyledDivider />
                        {get(elementsSaving, "elastic") ? (
                            <StyledLoadingPlaceholder>
                                <CircularProgress color="primary" />
                            </StyledLoadingPlaceholder>
                        ) : propertySearch.searchResponse && propertySearch.searchResponse.results ? (
                            <React.Fragment>
                                <InfiniteScroll
                                    dataLength={propertySearch.searchResponse.results.length}
                                    next={loadMoreSearchResults}
                                    hasMore={Boolean(
                                        propertySearch.searchResponse.results.length <
                                        propertySearch.searchResponse.totalResultCount
                                    )}
                                    loader={<h4>Loading more results...</h4>}
                                >
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(4, 1fr)",
                                            gap: 2,
                                        }}
                                    >
                                        {Object.keys(propertySearch.searchResponse.results).map((property) => {
                                            return (
                                                <Box
                                                    key={propertySearch.searchResponse.results[property]["property_id"]}
                                                >
                                                    <StyledLb
                                                        to={`/propdetail/${propertySearch.searchResponse.results[property]["property_id"]}`}
                                                    >
                                                        <div className="propertyCard">
                                                            <div
                                                                className="propertyImageContainer"
                                                                style={{
                                                                    backgroundImage: `${
                                                                        propertySearch.searchResponse.results[property]
                                                                            .property_photo
                                                                            ? `url(${propertySearch.searchResponse.results[property].property_photo})`
                                                                            : `url(${noPhotoUrl})`
                                                                    }`,
                                                                }}
                                                            >
                                                                {propertySearch.searchResponse.results[property]
                                                                    .km_managed_flg && (
                                                                    <span className="propertyTag">KM Managed</span>
                                                                )}
                                                            </div>
                                                            <div className="propertyTitle">
                                                                {
                                                                    propertySearch.searchResponse.results[property]
                                                                        .property_name
                                                                }
                                                            </div>
                                                            <div className="propertyLocation">
                                                                {
                                                                    propertySearch.searchResponse.results[property]
                                                                        .property_address
                                                                }
                                                                , {propertySearch.searchResponse.results[property].city}
                                                                ,{" "}
                                                                {
                                                                    propertySearch.searchResponse.results[property]
                                                                        .state_code
                                                                }
                                                            </div>
                                                            <div className="propertySpace">
                                                                {
                                                                    propertySearch.searchResponse.results[property]
                                                                        .num_listings
                                                                }{" "}
                                                                {propertySearch.searchResponse.results[property]
                                                                    .num_listings === 1
                                                                    ? "listing"
                                                                    : "listings"}{" "}
                                                                -{" "}
                                                                {propertySearch.searchResponse.results[property]
                                                                    .sf_avail
                                                                    ? propertySearch.searchResponse.results[property]
                                                                          .sf_avail
                                                                    : "N/A"}{" "}
                                                                Available SF
                                                                <br />
                                                                {startCase(
                                                                    propertySearch.searchResponse.results[property]
                                                                        .property_type
                                                                )}
                                                            </div>
                                                        </div>
                                                    </StyledLb>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </InfiniteScroll>
                                {propertySearch.searchResponse.results === 0 ? (
                                    <StyledAlternateContent>Your search generated no results</StyledAlternateContent>
                                ) : (
                                    ""
                                )}
                            </React.Fragment>
                        ) : (
                            <StyledAlternateContent>Please apply search criteria</StyledAlternateContent>
                        )}
                    </React.Fragment>
                )}
            </StyledPaperSpacing>
            <SavedSearchesModal {...props} searchType="property" showModal={savedSearchModal} />
            <NameSearchModal {...props} searchType="property" showModal={nameSearchModal} />
        </React.Fragment>
    );
};

export default ReviewProperties;
